import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { resetPassword } from '~/lib/auth';
import { buildHead } from '~/seo/site';

type Search = { token?: string; error?: string };

export const Route = createFileRoute('/auth/reset-password')({
  head: () =>
    buildHead({
      title: 'Reset Password',
      description: 'Set a new password for your Prod Battle account.',
      path: '/auth/reset-password',
      noindex: true,
    }),
  component: ResetPassword,
  validateSearch: (search: Record<string, unknown>): Search => ({
    token: typeof search.token === 'string' ? search.token : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
});

function ResetPassword() {
  const { token, error } = Route.useSearch();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // The API redirects here with `?error=...` when the token has already
  // been used, expired, or otherwise rejected. Surface that up front so
  // the user sees a useful message instead of a broken form.
  if (error || !token) {
    return (
      <section className="mx-auto max-w-md px-4 py-16 space-y-4">
        <h2 className="text-2xl font-bold">Reset link no longer valid</h2>
        <p className="text-muted-foreground text-sm">
          {error
            ? 'This reset link has expired or already been used.'
            : 'This page needs a reset link from an email. If you came here by mistake, request a new one below.'}
        </p>
        <Link
          to="/auth/forgot-password"
          className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          Request a new link
        </Link>
      </section>
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    setPending(true);
    try {
      await resetPassword({ token: token as string, newPassword: password });
      // Send them to sign-in with a flag so we can flash a "password
      // updated, sign in" toast once we wire one up.
      navigate({ to: '/auth/sign-in', search: { reset: 1 } as never });
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="mx-auto max-w-md px-4 py-16 space-y-6">
      <header>
        <h2 className="text-2xl font-bold">Choose a new password</h2>
        <p className="text-muted-foreground text-sm">
          Pick something you haven't used before. Minimum 8 characters.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">New password</span>
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">Confirm new password</span>
          <input
            type="password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>
        {err && <p className="text-sm text-red-500">{err}</p>}
        <button
          type="submit"
          disabled={pending}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {pending ? 'Saving...' : 'Set new password'}
        </button>
      </form>
    </section>
  );
}
